import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth-store'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

const routes: Array<RouteRecordRaw> = [
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'events',
        path: 'events',
        component: () => import('../pages/admin/events/EventsPage.vue'),
      },
      {
        name: 'chat',
        path: 'chat',
        component: () => import('../pages/chat/ChatPage.vue'),
      },
      {
        name: 'mobile-chat',
        path: 'chat/mobile',
        component: () => import('../pages/chat/MobileChatPage.vue'),
      },
      {
        name: 'contact',
        path: 'contact',
        component: () => import('../pages/contact/ContactPage.vue'),
      },
      {
        name: 'contact-merge',
        path: 'contact/merge',
        component: () => import('../pages/contact/MergeContactsPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'channel',
        path: 'channel',
        component: () => import('../pages/channel/ChannelPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'whatsapp-templates',
        path: 'whatsapp/templates',
        component: () => import('../pages/whatsapp/WhatsappTempletePage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'whatsapp-templates-create',
        path: 'whatsapp/templates/create',
        component: () => import('../pages/whatsapp/CreateWhatsappTemplatePage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'podcast',
        path: 'whatsapp/podcast/:jobId?',
        component: () => import('../pages/whatsapp/PodcastPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'broadcast-jobs',
        path: 'whatsapp/broadcast-jobs',
        component: () => import('../pages/whatsapp/BroadcastJobsPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'automation',
        path: 'automation',
        component: () => import('../pages/automation/AutomationPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'bot-prompt-configs',
        path: 'bot/prompt-configs',
        component: () => import('../pages/bot-config/PromptConfigsPage.vue'),
        meta: { roles: ['Admin'] },
      },
      // Registered before the `:id` route so `new` is not captured as an id.
      {
        name: 'bot-prompt-config-new',
        path: 'bot/prompt-configs/new',
        component: () => import('../pages/bot-config/PromptConfigEditorPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'bot-prompt-config-edit',
        path: 'bot/prompt-configs/:id',
        component: () => import('../pages/bot-config/PromptConfigEditorPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'bot-prompt-variables',
        path: 'bot/prompt-variables',
        component: () => import('../pages/bot-config/PromptVariablesPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'bot-pages',
        path: 'bot/pages',
        component: () => import('../pages/bot-config/BotPagesPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'agent',
        path: 'agent',
        component: () => import('../pages/agent/AgentPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'agent-schedule',
        path: 'agent-schedule',
        component: () => import('../pages/agent/AgentSchedulePage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'agent-chat-management',
        path: 'agent-chat-management',
        component: () => import('../pages/agent/AgentChatManagementPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'snap',
        path: 'snap',
        component: () => import('../pages/snap/SnapList.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'instagramCallBack',
        path: 'instagramCallBack',
        component: () => import('../pages/channel/InstagramCallbackPage.vue'),
      },

      {
        name: 'ingest',
        path: 'ingest',
        component: () => import('../pages/Ingest/IngestPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'contact-report',
        path: 'reports/contact',
        component: () => import('../pages/admin/reports/ContactReport.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'agent-report',
        path: 'reports/agent',
        component: () => import('../pages/admin/reports/AgentReport.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'response-time-report',
        path: 'reports/response-time',
        component: () => import('../pages/admin/reports/RespondReport.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'message-report',
        path: 'reports/message',
        component: () => import('../pages/admin/reports/MessageReport.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'heatmap-report',
        path: 'reports/heatmap',
        component: () => import('../pages/admin/reports/MessageHeatmapReport.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'close-reason-report',
        path: 'reports/close-reason',
        component: () => import('../pages/admin/reports/ChatCloseReasonReport.vue'),
        meta: { roles: ['Admin'] },
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    name: 'privacy-policy',
    path: '/privacy-policy',
    component: () => import('../pages/privacy/PrivacyPolicy.vue'),
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'privacy-policy' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// Navigation guard to check authentication
router.beforeEach((to, from, next) => {
  // Move store access inside the guard to ensure Vue app is initialized
  const authStore = useAuthStore()
  const isLoggedIn = authStore.isLoggedIn

  // Check if the route is part of the auth layout
  const isAuthRoute = to.path.startsWith('/auth')

  // Define routes that don't require authentication
  const noAuthRequired = ['login', 'signup', 'recover-password', 'recover-password-email', 'privacy-policy']

  // Check if route requires authentication
  const requiresAuth = !isAuthRoute && !noAuthRequired.includes(to.name as string)

  if (requiresAuth && !isLoggedIn) {
    // User is not authenticated and route requires auth
    return next({ name: 'login' })
  }

  // Role-based access control: if route specifies roles, ensure user has at least one
  const requiredRoles = to.meta && (to.meta as any).roles ? ((to.meta as any).roles as string[]) : []
  if (requiredRoles.length > 0) {
    const hasRole = requiredRoles.some((r) => authStore.roles.includes(r))
    if (!hasRole) {
      // If logged in but not authorized, redirect to dashboard
      return next({ name: 'dashboard' })
    }
  }

  if (isLoggedIn && (to.name === 'login' || to.name === 'signup')) {
    // User is authenticated but trying to access login/signup pages
    return next({ name: 'dashboard' })
  } else {
    // No redirection needed, proceed normally
    return next()
  }
})

export default router
